import { useState, useEffect } from 'react'
import { Table, Button, Badge, Modal, Form, Row, Col, InputGroup } from 'react-bootstrap'
import { toast } from 'react-toastify'
import { blacklistApi } from '../services/api'
import { useConfirm } from '../components/common/ConfirmDialog'

interface BlacklistEntry {
  id: string
  number: string
  description: string
  block_type: string
  action: string
  is_active: boolean
  created_at: string
}

const emptyForm = {
  number: '',
  description: '',
  block_type: 'exact',
  action: 'hangup',
  is_active: true
}

const blockTypes = [
  { value: 'exact', label: 'Exakte Nummer' },
  { value: 'prefix', label: 'Präfix' },
  { value: 'regex', label: 'RegEx-Muster' },
  { value: 'anonymous', label: 'Anonyme Anrufer' }
]

const actions = [
  { value: 'hangup', label: 'Auflegen' },
  { value: 'busy', label: 'Besetzt-Ton' },
  { value: 'congestion', label: 'Überlastet' },
  { value: 'voicemail', label: 'Zu Voicemail' },
  { value: 'playback', label: 'Ansage abspielen' }
]

export default function Blacklist() {
  const confirm = useConfirm()
  const [entries, setEntries] = useState<BlacklistEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [editingEntry, setEditingEntry] = useState<BlacklistEntry | null>(null)
  const [formData, setFormData] = useState(emptyForm)
  const [search, setSearch] = useState('')

  useEffect(() => {
    loadEntries()
  }, [search])

  const loadEntries = async () => {
    try {
      const response = await blacklistApi.list({ search })
      setEntries(response.data.items || [])
    } catch {
      toast.error('Fehler beim Laden')
    } finally {
      setLoading(false)
    }
  }

  const openCreateModal = () => {
    setEditingEntry(null)
    setFormData(emptyForm)
    setShowModal(true)
  }

  const openEditModal = (entry: BlacklistEntry) => {
    setEditingEntry(entry)
    setFormData({
      number: entry.number,
      description: entry.description || '',
      block_type: entry.block_type || 'exact',
      action: entry.action || 'hangup',
      is_active: entry.is_active
    })
    setShowModal(true)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      if (editingEntry) {
        await blacklistApi.update(editingEntry.id, formData)
        toast.success('Eintrag aktualisiert')
      } else {
        await blacklistApi.create(formData)
        toast.success('Eintrag erstellt')
      }
      setShowModal(false)
      loadEntries()
    } catch {
      toast.error('Fehler beim Speichern')
    }
  }

  const handleDelete = async (entry: BlacklistEntry) => {
    const confirmed = await confirm({
      title: 'Blacklist-Eintrag löschen',
      message: `Eintrag "${entry.number}" wirklich löschen?`,
      confirmText: 'Löschen',
      variant: 'danger'
    })
    if (!confirmed) return
    try {
      await blacklistApi.delete(entry.id)
      toast.success('Eintrag gelöscht')
      loadEntries()
    } catch {
      toast.error('Fehler beim Löschen')
    }
  }

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleString('de-DE')
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4>Blacklist</h4>
        <div className="d-flex gap-2">
          <InputGroup style={{ width: '250px' }}>
            <InputGroup.Text><i className="bi bi-search"></i></InputGroup.Text>
            <Form.Control
              placeholder="Suchen..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </InputGroup>
          <Button variant="primary" onClick={openCreateModal}>
            <i className="bi bi-plus-lg me-1"></i> Nummer sperren
          </Button>
        </div>
      </div>

      <div className="table-container">
        <Table hover className="mb-0">
          <thead>
            <tr>
              <th>Nummer/Muster</th>
              <th>Beschreibung</th>
              <th>Typ</th>
              <th>Aktion</th>
              <th>Hinzugefügt</th>
              <th>Status</th>
              <th className="text-end">Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="text-center py-4">Laden...</td></tr>
            ) : entries.length === 0 ? (
              <tr><td colSpan={7} className="text-center py-4 text-muted">Keine gesperrten Nummern</td></tr>
            ) : (
              entries.map(e => (
                <tr key={e.id}>
                  <td><strong>{e.number}</strong></td>
                  <td>{e.description || <span className="text-muted">-</span>}</td>
                  <td><Badge bg="secondary">{blockTypes.find(t => t.value === e.block_type)?.label || e.block_type}</Badge></td>
                  <td><Badge bg="warning">{actions.find(a => a.value === e.action)?.label || e.action}</Badge></td>
                  <td className="small text-muted">{formatDate(e.created_at)}</td>
                  <td><Badge bg={e.is_active ? 'success' : 'secondary'}>{e.is_active ? 'Aktiv' : 'Inaktiv'}</Badge></td>
                  <td className="text-end">
                    <Button variant="outline-primary" size="sm" className="me-1" onClick={() => openEditModal(e)}>
                      <i className="bi bi-pencil"></i>
                    </Button>
                    <Button variant="outline-danger" size="sm" onClick={() => handleDelete(e)}>
                      <i className="bi bi-trash"></i>
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>

      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{editingEntry ? 'Eintrag bearbeiten' : 'Nummer sperren'}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Nummer/Muster *</Form.Label>
              <Form.Control
                value={formData.number}
                onChange={(e) => setFormData({...formData, number: e.target.value})}
                required
                placeholder={formData.block_type === 'regex' ? 'z.B. ^\\+49170.*' : 'z.B. +49301234567'}
              />
              <Form.Text className="text-muted">
                {formData.block_type === 'prefix' && 'Alle Nummern, die mit diesem Präfix beginnen'}
                {formData.block_type === 'regex' && 'Regulärer Ausdruck zum Matching'}
                {formData.block_type === 'anonymous' && 'Für anonyme Anrufer verwenden Sie "*"'}
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Beschreibung</Form.Label>
              <Form.Control
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="z.B. Spam-Anrufer"
              />
            </Form.Group>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Block-Typ</Form.Label>
                  <Form.Select
                    value={formData.block_type}
                    onChange={(e) => setFormData({...formData, block_type: e.target.value})}
                  >
                    {blockTypes.map(t => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Aktion</Form.Label>
                  <Form.Select
                    value={formData.action}
                    onChange={(e) => setFormData({...formData, action: e.target.value})}
                  >
                    {actions.map(a => (
                      <option key={a.value} value={a.value}>{a.label}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Check
                type="switch"
                id="is_active"
                label="Aktiv"
                checked={formData.is_active}
                onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>Abbrechen</Button>
            <Button variant="primary" type="submit">
              {editingEntry ? 'Speichern' : 'Sperren'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  )
}
